import React, { useContext, useState } from "react";
import { Container } from "@material-ui/core";
import PageTitle from "../../../components/pageTitle/PageTitle";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Calendar, CheckSquare, ChevronRight, Clock, DollarSign, FileText, Home, MailX, MessageSquare, Package, PackageX, RefreshCw, TrendingUp, Users, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";

function ClientReport(props) {
const navigate = useNavigate();

    const reportCategories = [
    {
      title: "Appointment & Schedule Health",
      reports: [
        {
          title: "Upcoming Appointments",
          description: "Shows all upcoming appointments by client",
          icon: <Calendar className="h-6 w-6" />,
          to: "/reports/newreport/upcomingAppointments"
        },
        {
          title: "Cancellations & Reschedules",
          description: "Tracks appointment changes",
          icon: <XCircle className="h-6 w-6" />,
          to: "/reports/newreport/cancelAndReschduleAppointment"
        },
        {
          title: "Repeat Visits & No-Shows",
          description: "Shows repeat jobs or missed visits",
          icon: <RefreshCw className="h-6 w-6" />,
          to: "/reports/newreport/repeatVisitsNoShows"
        },
        {
          title: "Most Active Clients",
          description: "Ranks clients by appointment volume",
          icon: <Users className="h-6 w-6" />,
          to: "/reports/newreport/mostActiveClient"
        }
      ]
    },
    {
      title: "Task Completion by Client",
      reports: [
        {
          title: "Task Completion Summary",
          description: "Compares assigned vs completed tasks per client site",
          icon: <CheckSquare className="h-6 w-6" />,
          to: "/reports/newreport/taskCompletionSummary"
        },
        {
          title: "Overdue Tasks",
          description: "Displays tasks completed past their due time",
          icon: <Clock className="h-6 w-6" />,
          // to: "/reports/client/overdue-tasks"
        }
      ]
    },
    {
      title: "Issues & Resolution",
      reports: [
        {
          title: "Client Issue Trends",
          description: "Issue volume and categories by client",
          icon: <TrendingUp className="h-6 w-6" />,
          to: "/reports/newreport/clientIssueTrends"
        },
        {
          title: "Avg. Resolution Time",
          description: "Average time to resolve issues per client",
          icon: <AlertCircle className="h-6 w-6" />,
          // to: "/reports/client/avg-resolution-time"
        }
      ]
    },
    {
      title: "Supply Requests per Client",
      reports: [
        {
          title: "Supply Usage Report",
          description: "Lists supplies requested per site",
          icon: <Package className="h-6 w-6" />,
          // to: "/reports/client/supply-usage-report"
        },
        {
          title: "Urgent Requests Summary",
          description: "Breaks down emergency supply requests",
          icon: <PackageX className="h-6 w-6" />,
          // to: "/reports/client/urgent-requests-summary"
        }
      ]
    },
    {
      title: "Communication Logs",
      reports: [
        {
          title: "Client Notification Log",
          description: "All messages sent to each client",
          icon: <MessageSquare className="h-6 w-6" />,
          // to: "/reports/client/notification-log"
        },
        {
          title: "Delivery Issues",
          description: "Failed or bounced emails/SMS",
          icon: <MailX className="h-6 w-6" />,
          // to: "/reports/client/delivery-issues"
        }
      ]
    },
    {
      title: "Invoicing / Billing Reports",
      reports: [
        {
          title: "Invoice Summary",
          description: "Shows invoices sent, paid, and outstanding",
          icon: <FileText className="h-6 w-6" />,
          // to: "/reports/client/invoice-summary"
        },
        {
          title: "Revenue by Client",
          description: "Total invoiced and paid by each client",
          icon: <DollarSign className="h-6 w-6" />,
          // to: "/reports/client/revenue-by-client"
        }
      ]
    }
  ];

  const handleNavigate = (to) => {
    navigate(to);
  };


  return (
    <>

     <Container style={{ boxSizing: "content-box", padding:0}} >
        <PageTitle
          pageTitle="Client Reports"
          showBreadcrumb={[{ label: "Home", to: "/" }, { label: "Reports" },  { label: "New Reports" }]}
          BreadCrumbCurrentPageTitle={"Client Reports"}
          showLastSeen={true}
        ></PageTitle>


        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="space-y-6">
            <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
              <p className="text-lg text-gray-900 dark:text-white">
                Generate and view reports for client activities and services
              </p>
            </div>

            <div className="space-y-8">
              {reportCategories?.map((category, categoryIndex) => (
                <div key={categoryIndex} className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-8">
                    {category?.title}
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {category?.reports?.map((report, reportIndex) => (
                      <Card key={reportIndex} className={`dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] hover:shadow-md transition-all duration-200 ${report?.to 
                        ? "hover:border-green-500 hover:bg-white dark:bg-[#1A1F2C] dark:hover:bg-gray-800 cursor-pointer group"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"}`}>
                        <CardHeader className="p-12">
                          <div className="flex items-start space-x-3">
                            <div className="p-8 bg-slate-100 dark:bg-gray-700 rounded-md text-slate-700 dark:text-gray-300 group-hover:bg-slate-100 dark:group-hover:bg-gray-700 group-hover:text-green-500 dark:group-hover:text-green-400 group-hover:ring-1 group-hover:ring-green-500 dark:group-hover:ring-green-400 transition-all">
                              {report?.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base font-medium text-gray-900 dark:text-white group-hover:text-green-500 dark:group-hover:text-green-400 break-words">
                                {report?.title}
                              </CardTitle>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0 pb-24 ph-24">
                          <CardDescription className="text-sm text-gray-500 dark:text-gray-400 mb-16 leading-relaxed">
                            {report?.description}
                          </CardDescription>
                        {report?.to ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-90 group-hover:border-green-500 group-hover:text-green-500 dark:group-hover:text-green-400 transition-colors"
                            onClick={() => handleNavigate(report?.to)}
                          >
                            Create Report
                          </Button>
                        ) : (
                          <div className="w-90 text-center py-2 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-md">
                            Coming Soon
                          </div>
                        )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </>
  )

}

export default ClientReport;